import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Check, Loader2 } from 'lucide-react';

const LanguageSelector = ({ variant = 'dropdown', className = '' }) => {
  const { language, setLanguage, loading, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages.find(l => l.code === language) || languages[0];

  const handleSelect = async (code) => {
    setIsOpen(false);
    if (code !== language) {
      await setLanguage(code);
    }
  };

  if (variant === 'full') {
    // Full settings-style selector
    return (
      <div data-testid="language-selector-full" data-no-translate="" className={`space-y-3 ${className}`}>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Language / भाषा</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              data-testid={`lang-option-${lang.code}`}
              onClick={() => handleSelect(lang.code)}
              disabled={loading}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                language === lang.code
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/40 hover:bg-muted/50'
              } ${loading ? 'opacity-50' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{lang.nativeName}</p>
                <p className="text-xs text-muted-foreground">{lang.name}</p>
              </div>
              {language === lang.code && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
              {loading && language !== lang.code && lang.code === language && <Loader2 className="h-4 w-4 animate-spin" />}
            </button>
          ))}
        </div>
        {loading && <p className="text-xs text-muted-foreground animate-pulse">Translating...</p>}
      </div>
    );
  }

  // Compact dropdown for header/navbar
  return (
    <div data-testid="language-selector" data-no-translate="" className={`relative ${className}`}>
      <button
        data-testid="language-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-muted/60 hover:bg-muted transition-colors border border-border"
        disabled={loading}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
        <span className="hidden sm:inline">{currentLang.nativeName}</span>
        <span className="sm:hidden">{currentLang.code.toUpperCase()}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-popover border border-border rounded-xl shadow-lg z-50 py-1 min-w-[180px]">
            {languages.map((lang) => (
              <button
                key={lang.code}
                data-testid={`lang-dropdown-${lang.code}`}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors ${
                  language === lang.code ? 'bg-primary/5 text-primary font-semibold' : 'text-foreground'
                }`}
              >
                <span className="flex-1 text-left">{lang.nativeName}</span>
                <span className="text-xs text-muted-foreground">{lang.name}</span>
                {language === lang.code && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
